import moment from "moment";
import { dataFormatters } from "../../../../utils/dataFormatters";
import { ToastService } from "../../../../utils/toastify";

// dummy dates for validation calculations
export const HOS_TEMPLATE_DATE_MAP = {
  "Current Day": [2000, 1, 1],
  "Next Day": [2000, 1, 2],
};

/**
 * Converts a date and time to a moment object with the specified timezone, taking into account if it should be the next day.
 * @param {string} date - The date string in a supported format.
 * @param {string} time - The time string in "HH:mm" format.
 * @param {string} timezone - The timezone to use for the converted moment object.
 * @param {boolean} isNextDay - Indicates if the date should be considered as the next day.
 * @returns {moment} The converted moment object with the specified date, time, and timezone.
 */

export function getDateFromDateTimeAndTimezone(date, time, timezone, isNextDay) {
  let momentDate = moment.tz(date, timezone).clone();
  if (isNextDay) {
    momentDate = momentDate.add(1, "day");
  }
  const [year, month, day] = momentDate.format().slice(0, 10).split("-");
  const [hour, min] = time.split(":");
  return moment
    .tz(timezone)
    .year(parseInt(year))
    .month(parseInt(month) - 1)
    .date(parseInt(day))
    .hour(parseInt(hour))
    .minute(parseInt(min))
    .second(0)
    .millisecond(0);
}

/**
 * Converts a date and time from an HOS entry template format to a moment object with the specified timezone.
 * @param {string} date - The date string in the HOS entry template format.
 * @param {string} time - The time string in "HH:mm" format.
 * @param {string} timezone - The timezone to use for the converted moment object.
 * @returns {moment} The converted moment object with the specified date, time, and timezone.
 */

export function getDateFromHosEntryTemplateDate(date, time, timezone) {
  const [year, month, day] = HOS_TEMPLATE_DATE_MAP[date];
  const [hour, min] = time.split(":");
  return moment
    .tz(timezone)
    .year(parseInt(year))
    .month(parseInt(month))
    .date(parseInt(day))
    .hour(parseInt(hour))
    .minute(parseInt(min))
    .second(0)
    .millisecond(0);
}

const HosTemplateEntryValidators = {
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateMinCriteriaForHosRecord: (record, warn = false) => {
    if (record.dutyType && record.startDate && record.endDate && record.startTime && record.endTime) {
      return true;
    }
    warn &&
      ToastService.Error(
        "Error: ",
        "End Date & Time missing for last the HOS record. Before adding a new one, please fill in the end time for last activity.",
      );
    return false;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordEndDateInPast: (record, warn = false) => {
    // endDate and endTime must be greater than the startDate and startTime of this record
    const { startDate, endDate, startTime, endTime, timezone } = record;
    const _startDate = getDateFromHosEntryTemplateDate(startDate, startTime, timezone);
    const _endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
    if (moment(_startDate).isAfter(moment(_endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be in the past!");
      return false;
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForConflicts: (record, lastRecord, warn = false) => {
    const { startDate, startTime, endDate, endTime, endLocCode, serviceType, timezone } = record;
    const _startDate = getDateFromHosEntryTemplateDate(startDate, startTime, timezone);
    const _endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
    if (record.dutyType !== "Interrupted Rest" && moment(_startDate).isSame(moment(_endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be the same as Start Date/Time!");
      return false;
    }
    // endDate and endTime must be greater than the startDate and startTime of this record
    if (moment(_startDate).isAfter(moment(_endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be in the past!");
      return false;
    }
    if (lastRecord) {
      const { endDate, endTime, timezone } = lastRecord;
      const __endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
      if (lastRecord.dutyType === "Interrupted Rest") {
        // startDate of current record must be greater than endDate of the last record
        if (moment(_startDate).isSameOrBefore(moment(__endDate))) {
          warn && ToastService.Error("Invalid Entry: ", "Cannot add an entry at the time of Interrupted Rest!");
          return false;
        }
      } else {
        // startDate of current record must be greater than or equal to endDate of the last record
        if (moment(_startDate).isBefore(moment(__endDate))) {
          warn && ToastService.Error("Invalid Entry: ", "Cannot add an entry before the last activity!");
          return false;
        }
      }
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForLocationConflict: (record, lastRecord, warn = false) => {
    const { startDate, endDate, startTime, endTime, startLocCode, serviceType, timezone } = record;
    const _startDate = getDateFromHosEntryTemplateDate(startDate, startTime, timezone);
    const _endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
    if (lastRecord) {
      const { endDate, endTime, timezone } = lastRecord;
      const __endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
      if (moment(_startDate).isSame(moment(__endDate)) && lastRecord.endLocCode && startLocCode !== lastRecord.endLocCode) {
        const loc = lastRecord.endLoc && lastRecord.endLoc.description;
        warn &&
          ToastService.Error(
            "Invalid Entry: ",
            `Start location of current '${serviceType}' must match the end location '${loc}' of previous '${lastRecord.serviceType}'!`,
          );
        return false;
      }
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForMoreThan24Hours: (record, warn = false) => {
    // the entered data must not be for more than 24 hours
    const { startDate, endDate, startTime, endTime, timezone } = record;
    const _startDate = getDateFromHosEntryTemplateDate(startDate, startTime, timezone);
    const _endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);
    if (_startDate && _endDate && moment(_endDate).diff(moment(_startDate), "hours") >= 24) {
      warn && ToastService.Error("Invalid Entry:", "Cannot add an HOS entry equal to or exceeding 24 hours.");
      return false;
    }
    return true;
  },
  /**
   * Validates that a Meal record has a duration of at least 30 minutes.
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateMealDuration: (record, warn = false) => {
    const { startDate, endDate, startTime, endTime, timezone } = record;

    const _startDate = getDateFromHosEntryTemplateDate(startDate, startTime, timezone);
    const _endDate = getDateFromHosEntryTemplateDate(endDate, endTime, timezone);

    const diffMinutes = moment(_endDate).diff(moment(_startDate), "minutes");
    const valid = diffMinutes >= 30;

    if (!valid && warn) {
      ToastService.Error("Error: ", "Meals 29 minutes or less must be added as Normal Covered Service.");
    }

    return valid;
  },
};
export default HosTemplateEntryValidators;



// WEBPACK FOOTER //
// ./src/components/HoursOfService/Helper/HosTemplateEntryValidators/index.js